import { useMemo } from 'react';
import type { CertificateParams } from 'shared';
import { CERTIFICATE_PARAM_LABELS, EASTER_EGG_THRESHOLD } from 'shared';

interface RadarChartProps {
  params: CertificateParams;
  size?: number;
  showLabels?: boolean;
  animated?: boolean;
}

const PARAM_KEYS: (keyof CertificateParams)[] = [
  'analytical',
  'intuition',
  'persistence',
  'observation',
  'communication',
  'deduction'
];

// 数値を表示用にフォーマット
function formatValue(value: number): string {
  if (value >= 100000000) {
    return `${Math.floor(value / 100000000)}億`;
  }
  if (value >= 10000) {
    return `${Math.floor(value / 10000)}万`;
  }
  return String(value);
}

export function RadarChart({
  params,
  size = 280,
  showLabels = true,
  animated = true
}: RadarChartProps) {
  const center = size / 2;
  const radius = size * 0.35;
  const labelRadius = size * 0.46;

  // イースターエッグが発動しているパラメータを検出
  const easterEggParams = useMemo(() => {
    return PARAM_KEYS.filter((key) => params[key] >= EASTER_EGG_THRESHOLD);
  }, [params]);

  const hasEasterEgg = easterEggParams.length > 0;

  // 6角形の頂点座標を計算（イースターエッグの場合は100でキャップ）
  const getPoint = (index: number, value: number): { x: number; y: number } => {
    const angle = (Math.PI * 2 * index) / 6 - Math.PI / 2;
    // イースターエッグの場合は視覚的に最大値で表示
    const cappedValue = Math.min(value, 100);
    const r = radius * (cappedValue / 100);
    return {
      x: center + r * Math.cos(angle),
      y: center + r * Math.sin(angle)
    };
  };

  // ラベル位置
  const getLabelPoint = (index: number): { x: number; y: number } => {
    const angle = (Math.PI * 2 * index) / 6 - Math.PI / 2;
    return {
      x: center + labelRadius * Math.cos(angle),
      y: center + labelRadius * Math.sin(angle)
    };
  };

  // グリッドライン（20%, 40%, 60%, 80%, 100%）
  const gridLevels = [20, 40, 60, 80, 100];

  // データポイントのパス
  const dataPath = useMemo(() => {
    const points = PARAM_KEYS.map((key, i) => getPoint(i, params[key]));
    return points.map((p, i) => (i === 0 ? `M ${p.x},${p.y}` : `L ${p.x},${p.y}`)).join(' ') + ' Z';
  }, [params, center, radius]);

  // グリッドのパス
  const gridPaths = gridLevels.map((level) => {
    const points = PARAM_KEYS.map((_, i) => getPoint(i, level));
    return points.map((p, i) => (i === 0 ? `M ${p.x},${p.y}` : `L ${p.x},${p.y}`)).join(' ') + ' Z';
  });

  // 軸ライン
  const axisLines = PARAM_KEYS.map((_, i) => {
    const p = getPoint(i, 100);
    return { x1: center, y1: center, x2: p.x, y2: p.y };
  });

  return (
    <div className="radar-chart-container">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="radar-chart">
        <defs>
          {/* P8-2b: 紫シアン → 骨董金のグラデ。stop-color は CSS プロパティなので
              style 経由で --shell-* トークン（shell-tokens.css）を解決させる（生 hex 直書きしない）。 */}
          <linearGradient id="radarGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: 'var(--shell-gold-bright)' }} stopOpacity="0.8" />
            <stop offset="100%" style={{ stopColor: 'var(--shell-gold-deep)' }} stopOpacity="0.8" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* グリッドライン */}
        {gridPaths.map((path, i) => (
          <path
            key={i}
            d={path}
            fill="none"
            stroke="rgba(255, 255, 255, 0.15)"
            strokeWidth="1"
          />
        ))}

        {/* 軸ライン */}
        {axisLines.map((line, i) => (
          <line
            key={i}
            x1={line.x1}
            y1={line.y1}
            x2={line.x2}
            y2={line.y2}
            stroke="rgba(255, 255, 255, 0.2)"
            strokeWidth="1"
          />
        ))}

        {/* データエリア */}
        <path
          d={dataPath}
          fill="url(#radarGradient)"
          fillOpacity="0.6"
          stroke="url(#radarGradient)"
          strokeWidth="2"
          filter="url(#glow)"
          className={animated ? 'radar-chart__data-path' : ''}
        />

        {/* データポイント */}
        {PARAM_KEYS.map((key, i) => {
          const point = getPoint(i, params[key]);
          return (
            <circle
              key={key}
              cx={point.x}
              cy={point.y}
              r="4"
              stroke="url(#radarGradient)"
              strokeWidth="2"
              className={animated ? 'radar-chart__point' : ''}
              style={{ animationDelay: `${i * 0.1}s`, fill: 'var(--shell-ivory)' }}
            />
          );
        })}
      </svg>

      {/* ラベル */}
      {showLabels && (
        <div className="radar-chart__labels" style={{ width: size, height: size }}>
          {PARAM_KEYS.map((key, i) => {
            const labelPos = getLabelPoint(i);
            const value = params[key];
            const isEasterEgg = value >= EASTER_EGG_THRESHOLD;
            return (
              <div
                key={key}
                className={`radar-chart__label ${isEasterEgg ? 'radar-chart__label--easter-egg' : ''}`}
                style={{
                  left: labelPos.x,
                  top: labelPos.y,
                  transform: 'translate(-50%, -50%)'
                }}
              >
                <span className="radar-chart__label-name">{CERTIFICATE_PARAM_LABELS[key]}</span>
                <span className={`radar-chart__label-value ${isEasterEgg ? 'radar-chart__label-value--easter-egg' : ''}`}>
                  {formatValue(value)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* イースターエッグ発動時のオーバーレイエフェクト */}
      {hasEasterEgg && (
        <div className="radar-chart__easter-egg-overlay" />
      )}
    </div>
  );
}

export default RadarChart;
